import React from 'react';

import { FormattedMessage } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';

import css from './LineItemCheckoutTotal.module.css';

/**
 * 'order' is a record of a completed payment, so it says what WAS paid. 'checkout' is an
 * agreement about to be made, and says "Subtotal before shipping" while the figure is still
 * incomplete — see D3.
 */
const totalLabelId = (variant, shippingPending) =>
  variant === 'order'
    ? 'TransactionPanel.totalPaid'
    : shippingPending
    ? 'CartOrderSummary.beforeShipping'
    : 'CartCheckoutPage.total';

/**
 * CL-CART-01 — the total row shown on the checkout pages, in place of the template's
 * `LineItemTotalPrice` for the customer.
 *
 * Two differences from the template's row, both borrowed from CartCheckoutPage:
 *
 *   1. It is "Total", not "Total price" — the basket checkout's wording.
 *   2. While shipping is still unpriced it reads "Subtotal before shipping". The template calls
 *      the same figure "Total price", which is wrong before a delivery address exists: the
 *      amount genuinely excludes shipping and is about to grow. Decision D3 says the price the
 *      buyer is shown is the price they are charged, so a figure that is not yet the total must
 *      not claim to be one.
 *
 * Uses CartCheckoutPage's own keys, so an operator editing that microcopy in Console changes
 * both checkouts at once.
 *
 * Provider totals are left to `LineItemTotalPrice` — "You'll earn" has its own completed and
 * refunded wording, none of which applies on a checkout page the seller never sees.
 *
 * @component
 * @param {Object} props
 * @param {propTypes.transaction} props.transaction
 * @param {boolean} props.shippingPending - true while no shipping fee has been priced
 * @param {string} props.variant - 'checkout' | 'order'
 * @param {intlShape} props.intl
 * @returns {JSX.Element|null}
 */
const LineItemCheckoutTotal = props => {
  const { transaction, shippingPending, variant, intl } = props;

  const totalPrice = transaction?.attributes?.payinTotal;
  if (!totalPrice) {
    return null;
  }

  return (
    <div className={css.payable}>
      <span>
        <FormattedMessage id={totalLabelId(variant, shippingPending)} />
      </span>
      <span className={css.payableAmount}>{formatMoney(intl, totalPrice)}</span>
    </div>
  );
};

export default LineItemCheckoutTotal;
